import { IModel, IAttribute, IViolation, ModelConstants, ModelMarker } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { Pipeline } from 'app/model/pipeline/pipeline';
import { format, sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { ContainerActivityCase } from './container-activity-case';
import { ForEachActivity } from './foreach.activity';
import { IfConditionActivity } from './if-condition.activity';
import { ValidationActivity } from './validation.activity';

@Metamodel.model({
    name: ModelConstants.switchActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.SwitchActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.SwitchActivityLabel)
    ],
    referable: true,
    validator:  SwitchActivity.unallowedTypesValidator
})
export class SwitchActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly EXPRESSION: IAttribute;
    private static readonly _maxCases: number = 25;

    @Metamodel.attribute({
        display: stringResources.Expression,
        templatable: true,
        accepts: ['String'],
        association: 'EXPRESSION',
        constraint: {
            required: true,
            validator: SwitchActivity.expressionValidator
        }
    })
    public expression: string;

     @Metamodel.attribute({
        display: stringResources.Cases,
        constraint: {
            required: true,
            validator: SwitchActivity.casesValidator
        }
    })
    public cases: ContainerActivityCase[] = [];

    @Metamodel.attribute({
        display: stringResources.Pipeline,
        type: Pipeline.MODEL
    })
    public defaultPipeline: Pipeline;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ControlActivity';
    }

    private static expressionValidator(switchActivity?: SwitchActivity): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (switchActivity.expression === '' || switchActivity.expression === undefined) {
            violations.push({
                context: switchActivity,
                message: () => stringResources.SwitchActivityEmptyExpressionError
            });
        }
        return violations.length ? violations : undefined;
    }

    private static casesValidator(switchActivity?: SwitchActivity): IViolation[] | undefined {
        const caseValuesSet = new Set();
        const violations: IViolation[] = [];
        let foundSwitchViolationEmptyCase = false;

        if (switchActivity.cases.length > SwitchActivity._maxCases) {
            violations.push({
                context: switchActivity,
                message: () => stringResources.SwitchActivityTooManyCasesError
            });
        }

        if (switchActivity.cases) {
            switchActivity.cases.forEach((switchCase) => {
                if (caseValuesSet.has(switchCase.value?.toUpperCase())) {
                    violations.push({
                        context: switchActivity,
                        message: () => sanitizeAndFormatString(stringResources.SwitchActivityCasesUniqueError, switchCase.value, switchActivity.name)
                    });
                }
                if ((switchCase.value === '' || switchCase.value === undefined) && !foundSwitchViolationEmptyCase) {
                    violations.push({
                        context: switchActivity,
                        message: () => stringResources.SwitchActivityEmptyCaseError
                    });
                    foundSwitchViolationEmptyCase = true;
                }
                caseValuesSet.add(switchCase.value.toUpperCase());
            });
        }

        return violations.length ? violations : undefined;
    }

    private static unallowedTypesValidator(switchActivity?: SwitchActivity): IViolation[] {
        const violations: IViolation[] = [];

        if (switchActivity) {
            if (switchActivity.defaultPipeline) {
                violations.push(...SwitchActivity.findUnallowedTypeViolation(switchActivity.defaultPipeline.activities));
            }

            if (switchActivity.cases) {
                switchActivity.cases.forEach((switchCase) => {
                    violations.push(...SwitchActivity.findUnallowedTypeViolation(switchCase.casePipeline.activities));
                });
            }
        }

        return violations;
    }

    private static findUnallowedTypeViolation(activities: Set<Activity>): IViolation[] {
        const unallowedTypes = [ForEachActivity, IfConditionActivity, SwitchActivity, ValidationActivity];
        const violations: IViolation[] = [];

        for (const activity of activities) {
            if (unallowedTypes.find((activityType) => activity instanceof activityType)) {
                const label = Metamodel.modelOf(activity).display[0];

                violations.push({
                    context: activity,
                    message: () => sanitizeAndFormatString(stringResources.SwitchActivityUnallowedActvitiyError, label, activity.name),
                    revalidate: () => ModelMarker.isDeleted(activity)
                });
            }
        }

        return violations;
    }

    public getSwitchCasePipelineName(switchCaseName?: string): string {
        if (switchCaseName) {
            return `CasePipeline${this.name}${switchCaseName.trim().replace(/[^a-zA-Z0-9]/g, '')}`;
        }
        return `${this.name}${ModelConstants.switchActivityDefaultCase}`;
    }

    public onActivityCloned(clonedActivity: SwitchActivity, context: { inlineDataset?: boolean }): void {
        if (this.defaultPipeline.activities != null) {
            this.defaultPipeline.activities.forEach((activity) => {
                const targetActivity = clonedActivity.defaultPipeline?.activities?.find((targetActivity) => targetActivity.name === activity.name);
                if (targetActivity != null && activity instanceof Activity) {
                    activity.onActivityCloned(targetActivity, context);
                }
            })
        }

        this.cases?.forEach((switchCase) => {
            const clonedSwitchCase = clonedActivity.cases?.find((sc) => sc.value === switchCase.value);
            switchCase.onActivityCloned(clonedSwitchCase, context);
        });
    }
}
